// src/features/affiliate/admin/OffersTab.tsx
// Admin: full offer catalog — create/edit payout terms & targeting, publish
// lifecycle, per-offer stats and postbacks.

import { useEffect, useState } from 'react';
import { observer } from '@react';
import { useAffiliateOffer, useI18n } from '@react/hooks';
import { Button, Alert } from '@react/components';
import type { Offer, OfferStats } from '@core/types/affiliate';
import { OfferFormDialog } from '../OfferFormDialog';
import { PostbacksPanel } from '../PostbacksPanel';
import { OfferStatusBadge, offerPayoutTypeLabel } from '../badges';
import { errMessage, formatMoney, listParams } from '../format';

export const OffersTab = observer(() => {
  const { t, locale } = useI18n();
  const store = useAffiliateOffer();

  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Offer | null>(null);
  const [stats, setStats] = useState<OfferStats | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Offer | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = () => {
    setError(null);
    store.ListOffers(listParams({ limit: 100 })).catch((e: unknown) => setError(errMessage(e, t('affiliate.admin.offers.loadError'))));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selected?.entity?.id) { setStats(null); return; }
    store.GetOfferStats(selected.entity.id).then((res) => setStats(res.stats ?? null)).catch(() => setStats(null));
  }, [selected?.entity?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleStatus = async (offer: Offer) => {
    if (!offer.entity) return;
    setBusyId(offer.entity.id);
    setError(null);
    try {
      if (offer.status === 'OFFER_STATUS_ACTIVE') {
        await store.PauseOffer(offer.entity.id, { entity: offer.entity });
      } else {
        await store.ActivateOffer(offer.entity.id, { entity: offer.entity });
      }
      load();
      if (selected?.entity?.id === offer.entity.id) {
        const fresh = await store.GetOffer(offer.entity.id);
        setSelected(fresh);
      }
    } catch (e) {
      setError(errMessage(e, t('affiliate.admin.offers.saveError')));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="flex gap-4">
      <div className="min-w-0 flex-1 space-y-3">
        <div className="flex justify-end">
          <Button size="sm" onClick={() => { setEditing(null); setFormOpen(true); }}>{t('affiliate.admin.offers.createTitle')}</Button>
        </div>

        {error && <Alert variant="error">{error}</Alert>}
        {store.isLoading && store.items.length === 0 && <div className="h-32 animate-pulse rounded-md bg-muted" aria-busy="true" />}
        {!store.isLoading && store.items.length === 0 && !error && (
          <div className="rounded-md border border-dashed border-border p-8 text-center">
            <p className="text-sm text-muted-foreground">{t('affiliate.offers.empty')}</p>
          </div>
        )}

        <div className="space-y-2">
          {store.items.map((o) => (
            <button
              key={o.entity?.id}
              type="button"
              onClick={() => setSelected(o)}
              className={`w-full rounded-md border p-3 text-left hover:bg-muted ${selected?.entity?.id === o.entity?.id ? 'border-primary' : 'border-border'}`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-foreground">{o.name}</span>
                <OfferStatusBadge status={o.status} />
              </div>
              <p className="mt-1 text-sm text-muted-foreground">{offerPayoutTypeLabel(t, o.payout_type)} · {o.active_affiliates_count} {t('affiliate.admin.offers.activeAffiliates')}</p>
            </button>
          ))}
        </div>
      </div>

      {selected && (
        <aside className="w-80 shrink-0 space-y-4 border-l border-border pl-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-foreground">{selected.name}</h3>
            <button onClick={() => setSelected(null)} aria-label={t('common.close')} className="rounded-md p-1 text-muted-foreground hover:bg-muted">
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
            </button>
          </div>

          <div className="flex gap-1.5">
            <Button size="sm" variant="secondary" onClick={() => { setEditing(selected); setFormOpen(true); }}>{t('common.edit')}</Button>
            <Button
              size="sm"
              variant={selected.status === 'OFFER_STATUS_ACTIVE' ? 'danger' : 'primary'}
              onClick={() => toggleStatus(selected)}
              isLoading={busyId === selected.entity?.id}
            >
              {selected.status === 'OFFER_STATUS_ACTIVE' ? t('affiliate.admin.offers.pause') : t('affiliate.admin.offers.activate')}
            </Button>
          </div>

          {stats && (
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div><p className="text-xs text-muted-foreground">{t('affiliate.dashboard.clicks')}</p><p className="text-foreground">{stats.clicks}</p></div>
              <div><p className="text-xs text-muted-foreground">{t('affiliate.dashboard.conversions')}</p><p className="text-foreground">{stats.conversions}</p></div>
              <div><p className="text-xs text-muted-foreground">{t('affiliate.admin.offers.revenue')}</p><p className="text-foreground">{formatMoney(stats.revenue, locale)}</p></div>
              <div><p className="text-xs text-muted-foreground">{t('affiliate.admin.offers.cost')}</p><p className="text-foreground">{formatMoney(stats.cost, locale)}</p></div>
            </div>
          )}

          <div className="border-t border-border pt-3">
            {selected.entity?.id && <PostbacksPanel offerId={selected.entity.id} />}
          </div>
        </aside>
      )}

      <OfferFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        offer={editing}
        onSaved={(saved) => { setSelected(saved); load(); }}
      />
    </div>
  );
});
